// Hideo - script editor: paste from other pages, Google Docs and Word, and automatic links. pasteCopy copies only the
// allowed elements into a new tree (no styles, classes or attributes of the source page; links only http(s) and
// mailto), pasteTidy removes the empty and doubled blocks, autoLinkIn turns written addresses into links. The paste
// event itself (clipboard, selection, insertHTML) is src/features/editor/surface/clipboard.ts. Ported with the same results
// (golden tests: tests/editor-paste.test.ts against tests/fixtures/legacy/editor.js).
import { normUrl } from '../../utils/url'

/** Source element → element written in the script (anything else: its content only) */
export const PASTE_TAG: Readonly<Record<string, string>> = { P: 'p', DIV: 'p', SECTION: 'p', ARTICLE: 'p', HEADER: 'p', FOOTER: 'p', MAIN: 'p', TR: 'p', DT: 'p', DD: 'p', FIGCAPTION: 'p', H1: 'h1', H2: 'h2', H3: 'h3', H4: 'h4', H5: 'h4', H6: 'h4', UL: 'ul', OL: 'ol', LI: 'li', BLOCKQUOTE: 'blockquote', PRE: 'pre', CODE: 'code', STRONG: 'strong', B: 'strong', EM: 'em', I: 'em', U: 'u', S: 's', STRIKE: 's', DEL: 's', A: 'a', BR: 'br', HR: 'hr' }
/** Source elements dropped with their content */
export const PASTE_SKIP = /^(SCRIPT|STYLE|META|LINK|TITLE|HEAD|IMG|PICTURE|SVG|VIDEO|AUDIO|CANVAS|IFRAME|OBJECT|EMBED|BUTTON|SELECT|TEXTAREA|NOSCRIPT|TEMPLATE|FORM)$/

/** A link the editor accepts: http(s) (also written as www.…) or mailto, else '' */
export const linkHref = (u: unknown): string => {
  const s = normUrl(String(u || '').replace(/^www\./i, 'https://www.'))
  return /^(https?:\/\/[^\s]+|mailto:[^\s]+)$/i.test(s) ? s : ''
}
/** A new link to an address, opening in a new tab without access to the editor's page */
export function newLink(href: string): HTMLAnchorElement {
  const a = document.createElement('a'); a.href = href; a.target = '_blank'; a.rel = 'noopener'
  return a
}
/** A lone web address (the whole text): pasted, it becomes a link */
export const LONE_URL = /^(https?:\/\/|www\.)\S+$/i
/** Addresses written in the text (a new global pattern at every call: it keeps the position of the last match) */
export const urlRe = (): RegExp => /\b((?:https?:\/\/|www\.)[^\s<>"']*[^\s<>"'.,;:!?)\]}])/gi

type Out = Element | DocumentFragment

/** Copies only the allowed elements of src into out; from Google Docs and Word it keeps bold, italic, underline and
 *  strikethrough written as styles */
export function pasteCopy(src: ParentNode, out: Out): void {
  for (const c of src.childNodes) {
    if (c.nodeType === 3) { out.appendChild(document.createTextNode(c.parentElement && c.parentElement.closest('pre') ? c.textContent as string : (c.textContent as string).replace(/[\r\n\t]+/g, ' '))); continue }
    if (c.nodeType !== 1 || PASTE_SKIP.test((c as Element).tagName)) continue
    const e = c as HTMLElement, T = e.tagName, st = (e.style || {}) as Partial<CSSStyleDeclaration>
    if (T === 'INPUT') { const inp = e as HTMLInputElement; if (inp.type === 'checkbox' && (out as Element).tagName === 'LI' && (inp.checked || inp.hasAttribute('checked'))) (out as Element).setAttribute('data-done', '1'); continue }
    if (T === 'TD' || T === 'TH') { pasteCopy(e, out); out.appendChild(document.createTextNode('  ')); continue }
    let tag: string | null = PASTE_TAG[T]
    if (T === 'B' && /^(normal|[1-5]00)$/.test(st.fontWeight as string)) tag = null   // Google Docs wraps everything in <b style="font-weight:normal">
    let el: HTMLElement | null = tag ? document.createElement(tag) : null
    if (tag === 'a') { const h = linkHref(e.getAttribute('href')); if (h) { const a = el as HTMLAnchorElement; a.href = h; a.target = '_blank'; a.rel = 'noopener' } else el = null }
    if (tag === 'ul' && (e.classList.contains('todo') || e.querySelector(':scope > li > input[type=checkbox]'))) (el as HTMLElement).className = 'todo'
    if (tag === 'li' && e.hasAttribute('data-done')) (el as HTMLElement).setAttribute('data-done', '1')
    if (!el) {
      const wr: string[] = [], td = (st.textDecoration || '') + ' ' + (st.textDecorationLine || '')
      if (/^(bold|bolder|[6-9]00)$/.test(st.fontWeight as string)) wr.push('strong')
      if (st.fontStyle === 'italic') wr.push('em')
      if (/underline/.test(td)) wr.push('u')
      if (/line-through/.test(td)) wr.push('s')
      if (!wr.length) { pasteCopy(e, out); continue }
      let top: HTMLElement | null = null, cur: HTMLElement | null = null
      wr.forEach(k => { const x = document.createElement(k); if (cur) cur.appendChild(x); else top = x; cur = x })
      pasteCopy(e, cur as unknown as HTMLElement); out.appendChild(top as unknown as HTMLElement)
      continue
    }
    pasteCopy(e, el); out.appendChild(el)
  }
}

/** After pasteCopy: blocks inside paragraphs or headings lose the outer one (<div><div>… became paragraphs inside
 *  paragraphs), empty paragraphs, headings and items go, so do the blank text nodes between blocks and list items */
export function pasteTidy(root: ParentNode): void {
  const BLK = 'p,h1,h2,h3,h4,ul,ol,blockquote,pre,hr'
  root.querySelectorAll('p,h1,h2,h3,h4').forEach(el => { if (el.querySelector(BLK)) el.replaceWith(...el.childNodes) })
  root.querySelectorAll('p,h1,h2,h3,h4,li').forEach(el => { if (!(el.textContent as string).trim() && !el.querySelector('br,input,hr')) el.remove() })
  ;[root, ...root.querySelectorAll('ul,ol')].forEach(p => [...p.childNodes].forEach(n => { if (n.nodeType === 3 && !(n.textContent as string).trim()) n.remove() }))
}

/** After pasteTidy: the spaces of the source page's HTML layout, as its browser showed them. Line breaks and indents
 *  of the HTML (`<li>\n    text\n  </li>`, already spaces after pasteCopy) collapse into one space, and paragraphs,
 *  headings, quotes and list items lose the spaces at their start and end; at the start and end of what is pasted a
 *  space stays only if the copied text has one there (plain: the clipboard's text). Before, they became non-breaking
 *  spaces around the pasted words (user report, 24/10/2026). Code blocks keep their spaces. */
export function pasteSpaces(root: DocumentFragment | Element, plain = ''): void {
  const texts = (el: Node): Text[] => {
    const w = document.createTreeWalker(el, NodeFilter.SHOW_TEXT), out: Text[] = []
    for (let n = w.nextNode(); n; n = w.nextNode()) if (!(n.parentElement && n.parentElement.closest('pre'))) out.push(n as Text)
    return out
  }
  texts(root).forEach(n => { n.data = n.data.replace(/ {2,}/g, ' ') })
  const trim = (el: Node, start: boolean, end: boolean) => {
    const list = texts(el).filter(n => n.data)
    if (start && list.length) list[0].data = list[0].data.replace(/^ +/, '')
    const last = list[list.length - 1]
    if (end && last) last.data = last.data.replace(/ +$/, '')
  }
  root.querySelectorAll('p,h1,h2,h3,h4,li,blockquote').forEach(el => trim(el, true, true))
  trim(root, !/^\s/.test(plain), !/\s$/.test(plain))
}

/** Turns addresses written in the text into links (outside links and code) */
export function autoLinkIn(root: Node): void {
  const w = document.createTreeWalker(root, NodeFilter.SHOW_TEXT), list: Text[] = []
  for (let n = w.nextNode(); n; n = w.nextNode()) { const p = n.parentElement; if (!(p && p.closest('a,pre,code')) && urlRe().test(n.textContent as string)) list.push(n as Text) }
  list.forEach(n => {
    const f = document.createDocumentFragment(), txt = n.textContent as string
    let last = 0
    for (const m of txt.matchAll(urlRe())) {
      const h = linkHref(m[1])
      if (!h) continue
      f.appendChild(document.createTextNode(txt.slice(last, m.index)))
      const a = newLink(h); a.textContent = m[1]; f.appendChild(a)
      last = (m.index as number) + m[1].length
    }
    f.appendChild(document.createTextNode(txt.slice(last))); n.replaceWith(f)
  })
}
